// hooks/useCreateAuditLog.jsx
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { useNavigate } from "react-router-dom";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useCreateAuditLog
 * -------------------------------------------------
 * Responsible for creating audit/change logs via the manual create modal.
 *
 * Usage:
 * const { logs, setLogs } = useAuditLogs();
 * const { createAuditLog } = useCreateAuditLog(setLogs);
 * await createAuditLog({ ...payload });
 */
export function useCreateAuditLog(setLogs) {
  const auth = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const navigate = useNavigate();

  const createAuditLog = async (payload) => {
    if (!auth?.user) throw new Error("No authenticated user");

    const logData = {
      ...payload,
      logId: uuidv4(),
      companyId,
      implementer: {
        name: auth.user.name,
        email: auth.user.email,
        userId: auth.user.uid,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      await makeApiRequest(
        "post",
        apiRoutes.createAuditLog,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId,
        logData
      );

      // Optimistic update if setLogs provided
      if (setLogs) {
        setLogs((prev) => [logData, ...(prev || [])]);
      }

      toast({
        status: "success",
        title: "Change Created",
        description: logData.title,
        action: {
          label: "View log",
          onClick: () => navigate(`/audit/${logData.logId}`),
        },
      });

      return logData;
    } catch (err) {
      console.error("Failed to create audit log", err);
      toast({
        status: "destructive",
        title: "Failed to create log entry",
        description: err?.message || "Please try again.",
      });
      throw err;
    }
  };

  return { createAuditLog };
}
